import { Upload } from 'antd'
import { Button } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { UploadOutlined } from '@ant-design/icons'
import { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { useEffect, useState } from 'react'

export type AttachmentFieldType = Omit<UploadFile, 'uid'> & {
  isNew: boolean
  isDeleted: boolean
}

export interface UploadAttachmentForm {
  attachments: AttachmentFieldType[]
}

type UploadAttachmentsFormFieldProps = {
  value?: AttachmentFieldType[]
  onChange?: (newValue: AttachmentFieldType[]) => void
}

const UploadAttachmentsOnSaveInput = ({
  value = [],
  onChange = () => {},
}: UploadAttachmentsFormFieldProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [fileList, setFileList] = useState<AttachmentFieldType[]>(value)

  useEffect(() => {
    if (value !== fileList) {
      onChange(fileList)
    }
  }, [fileList])

  const handleChange = (info: UploadChangeParam<UploadFile>): void => {
    // if a file is deleted
    if (info.fileList.length < fileList.length) {
      const newFiles: AttachmentFieldType[] = fileList.map((file) => {
        if (file.url === info.file.url) {
          file.isDeleted = true
        }

        return file
      })

      setFileList(newFiles)

      return
    }

    const newFile = info.file as unknown as AttachmentFieldType
    newFile.isDeleted = false
    newFile.isNew = true

    const newFileList = [...fileList, newFile]

    setFileList(newFileList)
  }

  // I used 'as any' because of uuid error
  // uuid is not required in the docs
  // if it is not provided, it will be generated
  const notDeletedFileList: UploadFile[] = fileList.filter(
    (file) => file.isDeleted !== true
  ) as any

  return (
    <Upload
      fileList={notDeletedFileList}
      onChange={handleChange}
      // do not run any api query
      beforeUpload={() => {
        return false
      }}
    >
      <Button>
        <UploadOutlined />
        {t(incidentReportsConstants.CLICK_TO_UPLOAD)}
      </Button>
    </Upload>
  )
}

export const getNewFilesFormData = (
  attachments: AttachmentFieldType[]
): FormData | undefined => {
  if (!attachments || attachments.length === 0) {
    return undefined
  }

  const newAttachments = attachments.map((attachment) => {
    if (attachment.isNew === true) {
      return attachment
    }

    return undefined
  })

  const filteredAttachments = newAttachments.filter(
    (attachment) => attachment !== undefined
  ) as AttachmentFieldType[]

  if (filteredAttachments.length === 0) {
    return undefined
  }

  const formData = new FormData()
  filteredAttachments.forEach((attachment) => {
    if (!attachment || !(attachment instanceof File)) {
      throw new Error('No attachment')
    }
    formData.append('attachments', attachment, attachment.name)
  })

  return formData
}

export default UploadAttachmentsOnSaveInput
